/**
 * Client-side default ESTIMATE document layout — mirrors the backend
 * `buildDefaultEstimateDocLayout('ESTIMATE')` so the builder can open a brand-new
 * template instantly (both reference the same shared-types DEFAULT_* values).
 * A create sends this layout; the backend keeps it as-is.
 */
import {
  DEFAULT_DOCUMENT_BRANDING,
  DEFAULT_ESTIMATE_DOC_FOOTER,
  DEFAULT_ESTIMATE_DOC_HEADER,
  DEFAULT_ESTIMATE_DOC_PAGE_SETUP,
} from '@notifi/shared-types/estimate-doc-template';
import type { EstimateDocBlock, EstimateDocLayout } from '@/types/estimateDocTemplate';

/**
 * `scopeOfWork` became a block after some estimate designs were already saved.
 * Given a resolved ESTIMATE layout that predates it, inject the block (before the
 * line items, its default position) so scope of work renders on EVERY surface —
 * the hosted design link, the template PDF, and the builder — without a
 * per-renderer fallback. It renders nothing when the estimate has no scope, and
 * an author can hide/move it. Invoice layouts (no `estimateDetails`) are left
 * untouched — invoices carry no scope of work. Layouts that already have the
 * block, or lack a `blocks` array, are returned unchanged.
 */
export function ensureEstimateScopeBlock(layout: EstimateDocLayout): EstimateDocLayout {
  if (!Array.isArray(layout.blocks)) return layout;
  if (layout.blocks.some((b) => b.type === 'scopeOfWork')) return layout;
  if (!layout.blocks.some((b) => b.type === 'estimateDetails')) return layout;
  const blocks = [...layout.blocks].sort((a, b) => a.order - b.order);
  const anchor = blocks.findIndex((b) => b.type === 'itemGroups');
  const scopeBlock = {
    id: 'scopeOfWork',
    kind: 'content',
    type: 'scopeOfWork',
    order: 0,
  } as EstimateDocBlock;
  blocks.splice(anchor >= 0 ? anchor : blocks.length, 0, scopeBlock);
  return { ...layout, blocks: blocks.map((b, order) => ({ ...b, order }) as EstimateDocBlock) };
}

function buildDefaultEstimateBlocks(): EstimateDocBlock[] {
  const blocks: EstimateDocBlock[] = [
    { id: 'logo', kind: 'content', type: 'logo', order: 0 },
    {
      id: 'companyInfo',
      kind: 'content',
      type: 'companyInfo',
      order: 1,
      // Shares the letterhead row with the logo (logo left, company block right),
      // so the company block reads right-aligned against the sheet edge.
      inline: true,
      align: 'right',
      // Website off by default on the estimate letterhead (matches the classic
      // quote), mirroring the backend estimate default.
      fields: {
        address: { visible: true },
        phone: { visible: false },
        email: { visible: true },
        website: { visible: false },
      },
    },
    {
      id: 'billTo',
      kind: 'content',
      type: 'billTo',
      order: 2,
      fields: { email: { visible: true }, phone: { visible: false } },
    },
    // Bill-to · service address · details render as a three-column band.
    { id: 'jobAddress', kind: 'content', type: 'jobAddress', order: 3, inline: true },
    {
      id: 'estimateDetails',
      kind: 'content',
      type: 'estimateDetails',
      order: 4,
      inline: true,
      fields: {
        estimateNumber: { visible: false },
        date: { visible: true },
        validUntil: { visible: true },
      },
    },
    // Scope of work prose, before the line items — the position the classic
    // document uses.
    { id: 'scopeOfWork', kind: 'content', type: 'scopeOfWork', order: 5 },
    {
      id: 'itemGroups',
      kind: 'content',
      type: 'itemGroups',
      order: 6,
      heading: "What's included",
      fields: {
        description: { visible: true },
        quantity: { visible: true },
        unitPrice: { visible: true },
        lineAmount: { visible: true },
        itemImage: { visible: true },
      },
    },
    { id: 'totals', kind: 'content', type: 'totals', order: 7 },
    { id: 'deposit', kind: 'content', type: 'deposit', order: 8 },
    { id: 'membershipOffer', kind: 'content', type: 'membershipOffer', order: 9 },
    { id: 'notes', kind: 'content', type: 'notes', order: 10, heading: 'Notes' },
    { id: 'terms', kind: 'content', type: 'terms', order: 11, heading: 'Terms & Conditions' },
    { id: 'attachments', kind: 'content', type: 'attachments', order: 12 },
    { id: 'slot-approveDecline', kind: 'slot', type: 'approveDecline', order: 13 },
    { id: 'slot-payDeposit', kind: 'slot', type: 'payDeposit', order: 14 },
    { id: 'slot-signature', kind: 'slot', type: 'signature', order: 15 },
  ];
  return blocks;
}

export function buildDefaultEstimateLayout(): EstimateDocLayout {
  return {
    branding: { ...DEFAULT_DOCUMENT_BRANDING },
    pageSetup: { ...DEFAULT_ESTIMATE_DOC_PAGE_SETUP },
    header: { ...DEFAULT_ESTIMATE_DOC_HEADER },
    footer: { ...DEFAULT_ESTIMATE_DOC_FOOTER },
    blocks: buildDefaultEstimateBlocks(),
  };
}

/**
 * `jobAddress` (the service address) became part of the INVOICE layout after some
 * invoice designs were already saved. Given a resolved INVOICE layout that
 * predates it, inject the block into the bill-to band (right after `billTo`) so
 * the service address renders on every surface without a per-renderer fallback.
 * It renders nothing when the invoice has no job, and an author can hide/move it.
 * ESTIMATE/PROPOSAL layouts (they already carry `jobAddress`, and have
 * `estimateDetails`) are left untouched. Layouts already carrying the block, or
 * lacking a `blocks` array, are returned unchanged.
 */
export function ensureInvoiceJobBlocks(layout: EstimateDocLayout): EstimateDocLayout {
  if (!Array.isArray(layout.blocks)) return layout;
  if (layout.blocks.some((b) => b.type === 'jobAddress')) return layout;
  // Only INVOICE layouts (those carry `invoiceDetails`, never `estimateDetails`).
  if (!layout.blocks.some((b) => b.type === 'invoiceDetails')) return layout;
  const blocks = [...layout.blocks].sort((a, b) => a.order - b.order);
  const billToIdx = blocks.findIndex((b) => b.type === 'billTo');
  const jobAddressBlock = {
    id: 'jobAddress',
    kind: 'content',
    type: 'jobAddress',
    inline: true,
    order: 0,
  } as EstimateDocBlock;
  blocks.splice(billToIdx >= 0 ? billToIdx + 1 : 0, 0, jobAddressBlock);
  return { ...layout, blocks: blocks.map((b, order) => ({ ...b, order }) as EstimateDocBlock) };
}

/** Client-side default INVOICE layout — mirrors the backend INVOICE branch. */
function buildDefaultInvoiceBlocks(): EstimateDocBlock[] {
  return [
    { id: 'logo', kind: 'content', type: 'logo', order: 0 },
    {
      id: 'companyInfo',
      kind: 'content',
      type: 'companyInfo',
      order: 1,
      // Logo left, company block right — right-aligned against the sheet edge.
      inline: true,
      align: 'right',
      // The classic invoice letterhead prints phone + website (unlike the estimate).
      fields: {
        address: { visible: true },
        phone: { visible: true },
        email: { visible: true },
        website: { visible: true },
      },
    },
    {
      id: 'billTo',
      kind: 'content',
      type: 'billTo',
      order: 2,
      fields: { email: { visible: true }, phone: { visible: false } },
    },
    // Bill-to · service address · details render as a three-column band.
    { id: 'jobAddress', kind: 'content', type: 'jobAddress', order: 3, inline: true },
    {
      id: 'invoiceDetails',
      kind: 'content',
      type: 'invoiceDetails',
      order: 4,
      inline: true,
      fields: {
        invoiceNumber: { visible: true },
        date: { visible: true },
        dueDate: { visible: true },
        status: { visible: true },
        jobType: { visible: true },
        jobNumber: { visible: true },
        serviceDate: { visible: true },
      },
    },
    {
      id: 'itemGroups',
      kind: 'content',
      type: 'itemGroups',
      order: 5,
      heading: 'Services',
      fields: {
        description: { visible: true },
        quantity: { visible: true },
        unitPrice: { visible: true },
        lineAmount: { visible: true },
        itemImage: { visible: true },
      },
    },
    { id: 'totals', kind: 'content', type: 'totals', order: 6 },
    { id: 'balanceSummary', kind: 'content', type: 'balanceSummary', order: 7 },
    { id: 'paymentHistory', kind: 'content', type: 'paymentHistory', order: 8 },
    {
      id: 'additionalDetails',
      kind: 'content',
      type: 'additionalDetails',
      order: 9,
      heading: 'Additional Details',
    },
    { id: 'notes', kind: 'content', type: 'notes', order: 10, heading: 'Notes' },
    { id: 'terms', kind: 'content', type: 'terms', order: 11, heading: 'Terms & Conditions' },
    { id: 'remitTo', kind: 'content', type: 'remitTo', order: 12 },
    { id: 'slot-payInvoice', kind: 'slot', type: 'payInvoice', order: 13 },
  ];
}

export function buildDefaultInvoiceLayout(): EstimateDocLayout {
  return {
    branding: { ...DEFAULT_DOCUMENT_BRANDING },
    pageSetup: { ...DEFAULT_ESTIMATE_DOC_PAGE_SETUP },
    header: { ...DEFAULT_ESTIMATE_DOC_HEADER },
    footer: { ...DEFAULT_ESTIMATE_DOC_FOOTER },
    blocks: buildDefaultInvoiceBlocks(),
  };
}
